import { useAuthStore } from '@/stores/authStore';

export const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api';

interface RequestOptions extends RequestInit {
  data?: unknown;
}

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data: unknown
  ) {
    super(`${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

async function refreshTokens(): Promise<boolean> {
  const { refreshToken, setTokens, logout } = useAuthStore.getState();
  
  if (!refreshToken) {
    logout();
    return false;
  }

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      logout();
      return false;
    }

    const data = await response.json();
    setTokens(data.accessToken, data.refreshToken);
    return true;
  } catch {
    logout();
    return false;
  }
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { accessToken } = useAuthStore.getState();
  
  const headers: HeadersInit = {
    ...options.headers,
  };

  // Only set Content-Type for requests with body
  if (options.data) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }

  const config: RequestInit = {
    ...options,
    headers,
  };

  if (options.data) {
    config.body = JSON.stringify(options.data);
  }

  let response = await fetch(`${API_BASE}${endpoint}`, config);

  // Handle token expiration
  if (response.status === 401 && accessToken) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      // Retry request with new token
      const newToken = useAuthStore.getState().accessToken;
      (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_BASE}${endpoint}`, { ...config, headers });
    }
  }

  // Handle 404 on /auth/me - force logout and redirect
  if (response.status === 404 && endpoint === '/auth/me') {
    const { logout } = useAuthStore.getState();
    logout();
    window.location.href = '/login';
    throw new ApiError(404, 'Not Found', { error: 'User not found' });
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(response.status, response.statusText, data);
  }

  return response.json();
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'POST', data }),

  patch: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'PATCH', data }),

  put: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'PUT', data }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

